'use client'

import React, {
    forwardRef,
    HTMLAttributes,
    ReactElement,
    useId,
    useState,
    useImperativeHandle,
    useContext,
} from 'react'
import { DictionaryContext, TableContext } from '../../../../providers'
import { CollapseRowCell } from './CollapseRowCell'
import { CollapseContentCell } from './CollapseContentCell'
import { TableCellProps } from './TableCell'
import { TagColors, colorBackgroundClasses } from '../../../atoms'
import { TableRowProvider } from '../../../../providers/table/table-row-provider'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../../constants/iconButtonAriaLabelMessageKeys'
import { useTableRowExpandAnnouncement } from '../hooks/useTableRowExpandAnnouncement'

export interface TableRowProps extends Omit<HTMLAttributes<HTMLTableRowElement>, 'color'> {
    children?: React.ReactNode
    collapsedContent?: React.ReactNode
    onClick?: () => void
    color?: TagColors
    className?: string
    /** Represents the actual table row html element, not the forwardRef with toggle function */
    rowRef?: React.Ref<HTMLTableRowElement>
}

export interface TableRowRef {
    toggleIsCollapsed: () => void
    isCollapsed: boolean
}

export const TableRow = forwardRef<TableRowRef, TableRowProps>(
    ({ children, collapsedContent, color, onClick, rowRef, ...props }, ref) => {
        const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
        const expandAnnouncement = useTableRowExpandAnnouncement(collapsedContent, isCollapsed)
        const collapsedRegionId = useId()
        const dictionary = useContext(DictionaryContext)
        const { isCollapsible, isInline, isNested, hasBorder, columns, hasBackground } = useContext(TableContext)

        const toggleIsCollapsed = () => {
            setIsCollapsed((collapsed) => !collapsed)
        }

        useImperativeHandle(
            ref,
            () => ({
                toggleIsCollapsed,
                isCollapsed,
            }),
            [isCollapsed],
        )

        const childrenWithColumnProp = React.Children.map<React.ReactNode, React.ReactNode>(
            React.Children.toArray(children).filter((child) => !!child), // This filters out possible conditional table-cells. These conditional cells would otherwise be a child with value "false" and therefore mess up the indexing of the columns
            (element, index) => {
                if (React.isValidElement(element)) {
                    return React.cloneElement(element as ReactElement<TableCellProps>, {
                        column: columns[index],
                    })
                }
                return element
            },
        )

        const getClasses = (): string => {
            let classes = 'table-row border-border-default relative'
            if (isCollapsed) classes += ' border-b'
            if (isInline && hasBorder) classes += ' border-x'
            if (isNested || !hasBorder) classes += ' last:border-b-0'
            if (hasBackground && !color) classes += ` table-row--background-default`
            if (!!color) classes += ` ${colorBackgroundClasses[color]}`
            if (!!props.className) classes += ` ${props.className}`
            return classes
        }

        return (
            <TableRowProvider color={color}>
                <tr
                    {...props}
                    ref={rowRef}
                    className={getClasses()}
                    onClick={() => {
                        onClick && onClick()
                    }}
                >
                    {childrenWithColumnProp}
                    {isCollapsible && (
                        <CollapseRowCell
                            isCollapsed={isCollapsed}
                            toggleIsCollapsed={toggleIsCollapsed}
                            hasContent={!!collapsedContent}
                            expandAnnouncement={collapsedContent ? expandAnnouncement : undefined}
                            controlledRegionId={collapsedContent ? collapsedRegionId : undefined}
                        />
                    )}
                </tr>
                {collapsedContent && (
                    <CollapseContentCell
                        isCollapsed={isCollapsed}
                        regionId={collapsedRegionId}
                        regionAriaLabel={resolveIconButtonAriaLabel(
                            dictionary,
                            ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.TABLE_ROW_DETAILS,
                        )}
                    >
                        {collapsedContent}
                    </CollapseContentCell>
                )}
            </TableRowProvider>
        )
    },
)
